import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Fade,
  Skeleton,
  Drawer,
  Divider,
  Stack,
  Badge,
  Tooltip,
  Container,
  LinearProgress,
  Autocomplete,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FilterList,
  Refresh,
  TrendingUp,
  TrendingDown,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  People,
  Assessment,
  Schedule,
  AccountBalance,
  PersonAdd,
  ExitToApp,
  Close,
  CalendarMonth,
  Public,
  Apartment,
  SupportAgent,
  StorageTwoTone,
  Info,
  WarningAmber,
} from "@mui/icons-material";

import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";
import axios from "axios";
import { PhoneIncomingIcon, PhoneOutgoingIcon } from "lucide-react";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

const PIE_COLORS = ["#10b981", "#f59e0b", "#ef4444"];

// Helper function to get date ranges
const getDateRange = (filterType) => {
  const today = new Date();
  const start = new Date();
  const end = new Date();

  switch (filterType) {
    case "thisWeek": {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case "lastWeek": {
      const day = today.getDay();
      const diff = today.getDate() - day - 6;
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case "thisMonth": {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case "lastMonth": {
      start.setMonth(today.getMonth() - 1);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(today.getMonth());
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;
    }
    default:
      return null;
  }

  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const EmptyStateCard = ({ icon: Icon, title, description, actionButton }) => (
  <Box
    sx={{
      bgcolor: "white",
      borderRadius: 3,
      p: 3,
      textAlign: "center",
      border: "2px dashed #e5e7eb",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 300,
    }}
  >
    <Box
      sx={{
        mb: 2,
        p: 2,
        bgcolor: "#f0f9ff",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 48, color: "#0284c7" }} />
    </Box>
    <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
      {description}
    </Typography>
    {actionButton}
  </Box>
);

const AdminDashboard = () => {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState("custom");

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    agent: "",
    company: "",
    country: "",
  });

  useEffect(() => {
    getDashboardDetails();
  }, []);

  const getDashboardDetails = async (appliedFilters = filters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const res = await axiosInstance.get(
        apiEndPointsConfig.getDashboardDetails,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params,
        }
      );

      if (res.data.success) {
        setDashboardData(res.data.data);
      }
    } catch (error) {
      console.error(
        "Dashboard API error:",
        error.response?.data || error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateFilterTypeChange = (_, newType) => {
    if (newType) {
      setDateFilterType(newType);
      if (newType !== "custom") {
        const dateRange = getDateRange(newType);
        setFilters((prev) => ({
          ...prev,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }));
      }
    }
  };

  const handleApplyFilters = () => {
    getDashboardDetails(filters);
    setFilterDrawerOpen(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      startDate: "",
      endDate: "",
      agent: "",
      company: "",
      country: "",
    };
    setFilters(resetFilters);
    setDateFilterType("custom");
    getDashboardDetails(resetFilters);
  };

  const activeFiltersCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "").length,
    [filters]
  );

  const formatDurationHMS = (seconds = 0) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const KPICard = ({ title, value, icon, gradient, trend, subtitle }) => (
    <Fade in timeout={600}>
      <Box
        sx={{
          color: "white",
          position: "relative",
          overflow: "hidden",
          borderRadius: 3,
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
          background: "white",
          border: "1px solid #dddd",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  opacity: 0.9,
                  fontWeight: 600,
                  mb: 0.75,
                  color: "#141413",
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={800}
                sx={{ mb: 0.5, lineHeight: 1.05, color: "grey" }}
              >
                {value?.toLocaleString() || 0}
              </Typography>

              {typeof trend === "number" && (
                <Chip
                  icon={
                    trend > 0 ? (
                      <TrendingUp fontSize="small" />
                    ) : (
                      <TrendingDown fontSize="small" />
                    )
                  }
                  label={`${Math.abs(trend)}% vs last period`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "white",
                    fontWeight: 700,
                    mt: 1.25,
                    "& .MuiChip-icon": { color: "white" },
                  }}
                />
              )}
            </Box>
          </Box>
        </CardContent>

        <Box
          sx={{
            position: "absolute",
            bottom: -26,
            right: -24,
            opacity: 0.12,
            transform: "rotate(-8deg)",
            "& svg": { fontSize: 140 },
          }}
        >
          {icon}
        </Box>
      </Box>
    </Fade>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const p = payload[0];
      return (
        <Box
          sx={{
            bgcolor: "white",
            p: 1.5,
            borderRadius: 2,
            boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
            border: "1px solid #eef2f7",
          }}
        >
          <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5 }}>
            {p.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Count: {p.value}
          </Typography>
          {p?.payload?.total ? (
            <Typography variant="caption" color="text.secondary">
              {((p.value / p.payload.total) * 100).toFixed(1)}%
            </Typography>
          ) : null}
        </Box>
      );
    }
    return null;
  };

  // Loading skeleton (first load)
  if (loading && !dashboardData) {
    return (
      <Box sx={{ width: "100%", bgcolor: "#f6f7fb", minHeight: "100vh" }}>
        <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
          <AdminSideNav />
          <Box sx={{ display: { xs: "none", md: "initial" } }}>
            <AdminHeader title={"Dashboard Overview"} />
          </Box>

          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={220} height={40} />
                <Skeleton variant="text" width={420} height={24} />
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rounded" width={44} height={44} />
                <Skeleton variant="rounded" width={120} height={44} />
              </Box>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {[...Array(4)].map((_, idx) => (
                <Grid item xs={12} sm={6} md={3} key={`kpi1-${idx}`}>
                  <Skeleton
                    variant="rounded"
                    height={150}
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {[...Array(3)].map((_, idx) => (
                <Grid item xs={12} sm={6} md={4} key={`kpi2-${idx}`}>
                  <Skeleton
                    variant="rounded"
                    height={150}
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {[...Array(3)].map((_, idx) => (
                <Grid item xs={12} md={4} key={`chart-${idx}`}>
                  <Box
                    sx={{
                      bgcolor: "white",
                      borderRadius: 3,
                      p: 2.5,
                      boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                      height: 420,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Skeleton variant="circular" width={28} height={28} />
                      <Skeleton variant="text" width="55%" height={28} />
                    </Box>

                    <Skeleton
                      variant="rounded"
                      height={290}
                      sx={{ borderRadius: 3 }}
                    />

                    <Box
                      sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                    >
                      {[...Array(4)].map((__, i) => (
                        <Skeleton
                          key={i}
                          variant="rounded"
                          width={90}
                          height={28}
                          sx={{ borderRadius: 999 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 3,
                p: 2.5,
                boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="text" width={220} height={28} />
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                {[...Array(6)].map((_, idx) => (
                  <Skeleton key={idx} variant="text" width="16%" height={24} />
                ))}
              </Box>

              {[...Array(6)].map((_, row) => (
                <Box
                  key={row}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1.5,
                    borderTop: "1px solid #eef2f7",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "22%",
                    }}
                  >
                    <Skeleton variant="circular" width={34} height={34} />
                    <Skeleton variant="text" width="70%" height={22} />
                  </Box>

                  <Skeleton
                    variant="rounded"
                    width="10%"
                    height={28}
                    sx={{ borderRadius: 999 }}
                  />
                  <Skeleton variant="text" width="10%" height={22} />
                  <Skeleton variant="text" width="10%" height={22} />
                  <Skeleton variant="text" width="18%" height={22} />
                  <Skeleton
                    variant="rounded"
                    width="12%"
                    height={28}
                    sx={{ borderRadius: 999 }}
                  />
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      </Box>
    );
  }

  const hasData = dashboardData && dashboardData.totalCallLogs > 0;

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />
        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <AdminHeader title={"Dashboard "} />
        </Box>

        {/* Top bar actions */}
        <Container maxWidth="xl" sx={{ py: 2, bgcolor: "#FAF9F5" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={900}
                sx={{ color: "#0f172a" }}
              >
                Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Track users, calls, drop-offs, and agent performance.
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    onClick={() => getDashboardDetails(filters)}
                    disabled={loading}
                    sx={{
                      bgcolor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>

              <Badge badgeContent={activeFiltersCount} color="primary">
                <Button
                  variant="contained"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    borderRadius: 2,
                    px: 2.5,
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: "#6366F1",
                  }}
                >
                  Filters
                </Button>
              </Badge>
            </Box>
          </Box>

          {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 2 }} /> : null}

          {/* Active Filters Display */}
          {activeFiltersCount > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2, borderRadius: 2 }}
              icon={<Info />}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    Active Filters:
                  </Typography>
                  {Object.entries(filters)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <Chip
                        key={k}
                        label={`${k}: ${v}`}
                        size="small"
                        onDelete={() => {
                          const newFilters = { ...filters, [k]: "" };
                          setFilters(newFilters);
                          getDashboardDetails(newFilters);
                        }}
                        sx={{
                          bgcolor: "white",
                          color: "#0369a1",
                          fontWeight: 600,
                          "& .MuiChip-deleteIcon": {
                            color: "#0369a1",
                          },
                        }}
                      />
                    ))}
                </Box>
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Clear All
                </Button>
              </Box>
            </Alert>
          )}

          {/* KPI Cards - Row 1 */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Total Users"
                value={dashboardData?.totalUsers}
                icon={<People fontSize="large" />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Active Users"
                value={dashboardData?.paidCustomers}
                icon={<AccountBalance fontSize="large" />}
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                subtitle="Active subscriptions"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Demo Accounts"
                value={dashboardData?.demoAccounts}
                icon={<PersonAdd fontSize="large" />}
                gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                subtitle="Trial users"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Drop-off Accounts"
                value={dashboardData?.dropoffAccounts}
                icon={<ExitToApp fontSize="large" />}
                gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                subtitle="Inactive users"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Inbound Calls"
                value={dashboardData?.inboundCalls}
                icon={<PhoneIncomingIcon fontSize="large" />}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Outbound Calls"
                value={dashboardData?.outboundCalls}
                icon={<PhoneOutgoingIcon fontSize="large" />}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <KPICard
                title="Total Calls"
                value={dashboardData?.totalCallLogs}
                icon={<Phone fontSize="large" />}
                gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              />
            </Grid>
          </Grid>

          {/* Charts - Show empty state if no data */}
          {hasData ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#383838ff",
                          fontWeight: 550,
                        }}
                      >
                        Customer Segmentation
                      </Typography>
                    </Box>

                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dashboardData?.customerSegmentation || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          outerRadius={84}
                          dataKey="value"
                        >
                          {dashboardData?.customerSegmentation?.map(
                            (entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  entry.color ||
                                  PIE_COLORS[index % PIE_COLORS.length]
                                }
                              />
                            )
                          )}
                        </Pie>
                        <ReTooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>

                    <Box sx={{ mt: 2 }}>
                      {dashboardData?.customerSegmentation?.map(
                        (item, index) => (
                          <Box
                            key={index}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor:
                                    item.color ||
                                    PIE_COLORS[index % PIE_COLORS.length],
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ color: "#0f172a" }}
                              >
                                {item.name}
                              </Typography>
                            </Box>
                            <Chip label={item.value} size="small" />
                          </Box>
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#383838ff",
                          fontWeight: 550,
                        }}
                      >
                        Company Distribution
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Based on total calls
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        maxHeight: 390,
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      {dashboardData?.companyChartData?.length ? (
                        (() => {
                          const maxCalls = Math.max(
                            ...dashboardData.companyChartData.map(
                              (c) => c.calls
                            )
                          );

                          return dashboardData.companyChartData
                            .sort((a, b) => b.calls - a.calls)
                            .map((item, index) => {
                              const percent = (item.calls / maxCalls) * 100;

                              return (
                                <Box key={index} sx={{ mb: 2 }}>
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={0.5}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#0f172a",
                                        maxWidth: "70%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {item.company}
                                    </Typography>

                                    <Typography sx={{ color: "#6366f1" }}>
                                      {item.calls}
                                    </Typography>
                                  </Box>

                                  <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    sx={{
                                      height: 8,
                                      borderRadius: 6,
                                      bgcolor: "#eef2ff",
                                      cursor: "pointer",
                                      "& .MuiLinearProgress-bar": {
                                        borderRadius: 6,
                                        bgcolor:
                                          index === 0
                                            ? "#4f46e5"
                                            : index === 1
                                            ? "#6366f1"
                                            : "#a5b4fc",
                                      },
                                      "&:hover": {
                                        "& .MuiLinearProgress-bar": {
                                          filter: "brightness(0.9)",
                                        },
                                      },
                                    }}
                                    onClick={() => {
                                      console.log(
                                        "Clicked company:",
                                        item.company
                                      );
                                    }}
                                  />
                                </Box>
                              );
                            });
                        })()
                      ) : (
                        <EmptyStateCard
                          icon={Apartment}
                          title="No Company Data"
                          description="No calls found for the selected filters"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={12}>
                <Card
                  sx={{
                    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#383838ff",
                          fontWeight: 550,
                        }}
                      >
                        Drop-off Analysis
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 3, display: "block" }}
                    >
                      Excluding demo accounts
                    </Typography>

                    {dashboardData?.dropoffChartData?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData?.dropoffChartData || []}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#eef2f7"
                          />
                          <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <ReTooltip />
                          <Bar
                            dataKey="count"
                            radius={[10, 10, 0, 0]}
                            fill="#6d92ffff"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <EmptyStateCard
                        icon={WarningAmber}
                        title="No Drop-off Data"
                        description="No drop-off accounts found for the selected filters"
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ mb: 2 }}>
              <EmptyStateCard
                icon={Phone}
                title="No Call Data Available"
                description={
                  activeFiltersCount > 0
                    ? "No calls match the selected filters. Try adjusting your filter criteria."
                    : "No calls found in the system. Start making calls to see data here."
                }
                actionButton={
                  activeFiltersCount > 0 ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleResetFilters}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                      }}
                    >
                      Reset Filters
                    </Button>
                  ) : null
                }
              />
            </Box>
          )}

          {/* Agent Performance Table */}
          {dashboardData?.agentStats?.length > 0 ? (
            <Card
              sx={{
                boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#383838ff",
                      fontWeight: 550,
                    }}
                  >
                    Agent Performance
                  </Typography>
                </Box>

                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #eef2f7",
                    borderRadius: 3,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: "#f8fafc",
                          "& th": {
                            color: "#0f172a",
                            borderBottom: "2px solid #eef2f7",
                          },
                        }}
                      >
                        <TableCell>Agent Name</TableCell>
                        <TableCell align="center">Total Calls</TableCell>
                        <TableCell align="center">Inbound</TableCell>
                        <TableCell align="center">Outbound</TableCell>
                        <TableCell align="center">Total Duration</TableCell>
                        <TableCell align="center">Avg Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardData.agentStats.map((agent, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { bgcolor: "#f8fafc" },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell sx={{ py: 1, px: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography sx={{ color: "#0f172a" }}>
                                {agent.agentName}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                            <Chip
                              label={agent.totalCalls}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                            <Typography sx={{ color: "darkgreen" }}>
                              {agent.totalInbound}
                            </Typography>
                          </TableCell>

                          <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                            <Typography color="info.main">
                              {agent.totalOutbound}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                            {formatDurationHMS(agent.totalDuration)}
                          </TableCell>

                          <TableCell align="center" sx={{ py: 1, px: 1.5 }}>
                            <Chip
                              label={`${agent.avgDuration}s`}
                              size="small"
                              sx={{
                                bgcolor: "#e0f2fe",
                                color: "#0369a1",
                                fontWeight: 900,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : null}
        </Container>

        {/* FILTER DRAWER */}
        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "92vw", sm: 420 },
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
            },
          }}
        >
          <Box
            sx={{
              p: 2.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ color: "#0f172a" }}
                >
                  Filters
                </Typography>
              </Box>
              <IconButton
                onClick={() => setFilterDrawerOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                <Close />
              </IconButton>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
              <Stack spacing={2}>
                {/* Date Filter Type Selection */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1.5, fontWeight: 700, color: "#0f172a" }}
                  >
                    Quick Filters
                  </Typography>
                  <ToggleButtonGroup
                    value={dateFilterType}
                    exclusive
                    onChange={handleDateFilterTypeChange}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        py: 1,
                        border: "1px solid #e5e7eb",
                        "&.Mui-selected": {
                          bgcolor: "#6366f1",
                          color: "white",
                          borderColor: "#6366f1",
                          "&:hover": {
                            bgcolor: "#4f46e5",
                          },
                        },
                        "&:hover": {
                          bgcolor: "#f3f4f6",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="thisWeek">This Week</ToggleButton>
                    <ToggleButton value="lastWeek">Last Week</ToggleButton>
                    <ToggleButton value="thisMonth">This Month</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box>
                  <ToggleButtonGroup
                    value={dateFilterType}
                    exclusive
                    onChange={handleDateFilterTypeChange}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root": {
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        py: 1,
                        border: "1px solid #e5e7eb",
                        "&.Mui-selected": {
                          bgcolor: "#6366f1",
                          color: "white",
                          borderColor: "#6366f1",
                          "&:hover": {
                            bgcolor: "#4f46e5",
                          },
                        },
                        "&:hover": {
                          bgcolor: "#f3f4f6",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="lastMonth">Last Month</ToggleButton>
                    <ToggleButton value="custom">Custom</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Custom Date Pickers */}
                {dateFilterType === "custom" && (
                  <>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={filters.startDate}
                      onChange={(e) =>
                        handleFilterChange("startDate", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <CalendarMonth
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={filters.endDate}
                      onChange={(e) =>
                        handleFilterChange("endDate", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <CalendarMonth
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Other Filters */}
                <Autocomplete
                  options={dashboardData?.filters?.availableAgents || []}
                  value={filters.agent || null}
                  onChange={(_, newValue) =>
                    handleFilterChange("agent", newValue || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Agent"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SupportAgent sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  options={dashboardData?.filters?.availableCompanies || []}
                  value={filters.company || null}
                  onChange={(_, newValue) =>
                    handleFilterChange("company", newValue || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Apartment sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  options={dashboardData?.filters?.availableCountries || []}
                  value={filters.country || null}
                  onChange={(_, newValue) =>
                    handleFilterChange("country", newValue || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Public sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Box display="flex" gap={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                startIcon={<Assessment />}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  py: 1.1,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Apply
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  py: 1.1,
                  textTransform: "none",
                  fontWeight: 900,
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
